import React, { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useUserData } from '../context/UserDataContext';
import {
  X, Link2, Pencil, ChevronDown, ChevronUp, Check,
  Plus, Trash2, RefreshCw, AlertCircle, Zap,
} from 'lucide-react';
import {
  createLinkToken,
  exchangeToken,
  syncAllPlaidData,
  buildLinkedAccounts,
} from '../lib/plaidSync';

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCOUNT_TYPES = [
  { value: 'checking',    label: 'Checking',         category: 'bank',       nwType: 'Cash' },
  { value: 'savings',     label: 'Savings / HYSA',   category: 'bank',       nwType: 'Cash' },
  { value: 'brokerage',   label: 'Brokerage',        category: 'investment', nwType: 'Investment' },
  { value: '401k',        label: '401(k)',            category: 'investment', nwType: 'Retirement' },
  { value: 'roth_ira',    label: 'Roth IRA',         category: 'investment', nwType: 'Retirement' },
  { value: 'ira',         label: 'Traditional IRA',  category: 'investment', nwType: 'Retirement' },
  { value: 'credit_card', label: 'Credit Card',      category: 'liability',  nwType: null },
  { value: 'mortgage',    label: 'Mortgage',         category: 'liability',  nwType: null },
  { value: 'other',       label: 'Other',            category: 'other',      nwType: 'Cash' },
];


function isInvestmentType(type) {
  return ['brokerage', '401k', 'roth_ira', 'ira'].includes(type);
}

const inputCls = `w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200
  placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors`;

// ─── Exported helpers (used by other components) ──────────────────────────────

/** Badge showing linked vs manual connection type */
export function ConnectionBadge({ account, size = 'sm' }) {
  const isPlaid = account?.connectionType === 'plaid';
  const cls = size === 'xs'
    ? 'text-[10px] px-1.5 py-0.5 gap-1'
    : 'text-xs px-2 py-0.5 gap-1.5';
  return (
    <span className={`inline-flex items-center rounded-full font-medium flex-shrink-0 ${cls} ${
      isPlaid
        ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700/40'
        : 'bg-slate-700/60 text-slate-400 border border-slate-600/40'
    }`}>
      {isPlaid ? <><Zap size={9} className="text-emerald-400" /> Live</> : <><Pencil size={9} /> Manual</>}
    </span>
  );
}

/** Account type icon + label chip */
export function AccountTypeChip({ type }) {
  const def = ACCOUNT_TYPES.find(t => t.value === type);
  return (
    <span className="text-xs text-slate-500 bg-slate-700/40 px-2 py-0.5 rounded-full">
      {def?.label || type}
    </span>
  );
}

/** Dismissible "manually managed" banner for manual accounts.
 *  Call onDismiss to persist the dismissal. */
export function ManualAccountBanner({ account, onDismiss, onLinkPlaid }) {
  if (account?.connectionType !== 'manual' || account?.manualBannerDismissed) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-amber-950/30 border border-amber-700/30 rounded-lg text-xs text-amber-200/80">
      <Pencil size={11} className="text-amber-400 flex-shrink-0" />
      <span className="flex-1 min-w-0">
        This account is <span className="font-semibold text-amber-300">manually managed</span>. Want live updates instead?
      </span>
      {onLinkPlaid && (
        <button
          onClick={onLinkPlaid}
          className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-blue-600/80 hover:bg-blue-600 text-white rounded text-[10px] font-semibold transition-colors"
        >
          <Link2 size={9} /> Link with Plaid
        </button>
      )}
      <button onClick={onDismiss} className="flex-shrink-0 text-amber-500 hover:text-amber-300 transition-colors">
        <X size={12} />
      </button>
    </div>
  );
}

// ─── Real Plaid Link phase ────────────────────────────────────────────────────

function PlaidPhase({ plaidStep, plaidError, linkToken, onSuccess, onExit, onBack, onClose }) {
  const { open, ready } = usePlaidLink({
    token:     linkToken || null,
    onSuccess,
    onExit,
  });

  // Loading state
  if (plaidStep === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <div className="w-12 h-12 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
        <div className="text-sm text-slate-400">Initialising Plaid Link…</div>
      </div>
    );
  }

  // Linking / syncing states
  if (plaidStep === 'linking' || plaidStep === 'syncing') {
    const steps = [
      { key: 'linking',  label: 'Exchanging credentials' },
      { key: 'syncing',  label: 'Syncing accounts & transactions' },
    ];
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-5 text-center">
        <div className="w-16 h-16 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
        <div>
          <div className="font-semibold text-slate-200 text-sm">
            {plaidStep === 'linking' ? 'Linking your account…' : 'Syncing your data…'}
          </div>
          <div className="text-xs text-slate-500 mt-1">This may take a few seconds</div>
        </div>
        <div className="flex flex-col gap-1.5">
          {steps.map((s) => (
            <div key={s.key} className={`flex items-center gap-2 text-xs ${
              s.key === plaidStep ? 'text-blue-300' : 'text-slate-600'
            }`}>
              {s.key === plaidStep
                ? <RefreshCw size={10} className="animate-spin" />
                : <Check size={10} className="text-emerald-500" />}
              {s.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Success state
  if (plaidStep === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-5 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-900/40 border-2 border-emerald-600/50 flex items-center justify-center">
          <Check size={28} className="text-emerald-400" />
        </div>
        <div>
          <div className="font-bold text-slate-100 text-base">Account linked!</div>
          <div className="text-sm text-slate-400 mt-1">
            Your transactions, balances, and holdings have been imported.
          </div>
        </div>
        <div className="text-xs text-slate-500 space-y-1">
          <div>✓ Budget auto-populated with real transactions</div>
          <div>✓ Net Worth updated with account balances</div>
          <div>✓ Investing updated with holdings</div>
        </div>
        <button
          onClick={onClose}
          className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-xl transition-colors"
        >
          Done
        </button>
      </div>
    );
  }

  // Error state
  if (plaidStep === 'error' && plaidError) {
    return (
      <div className="space-y-5">
        <div className="flex items-start gap-3 p-4 bg-rose-950/30 border border-rose-700/40 rounded-xl">
          <AlertCircle size={16} className="text-rose-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-rose-300">Connection failed</div>
            <div className="text-xs text-slate-400 mt-1">{plaidError}</div>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={onBack}
            className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-xl transition-colors">
            ← Back
          </button>
          <button onClick={() => window.location.reload()}
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition-colors">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Ready state — show "Open Plaid Link" button
  return (
    <div className="space-y-5">
      {/* Plaid header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#111827] rounded-xl border border-slate-700/40">
        <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
          <span className="text-white font-black text-xs">P</span>
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold text-white">Plaid Link</div>
          <div className="text-[10px] text-slate-400">Finovo uses Plaid to connect to your bank</div>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-emerald-400">
          <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
          Secure
        </div>
      </div>

      <div className="space-y-3 p-4 bg-slate-900/60 rounded-xl border border-slate-700/60">
        <p className="text-sm text-slate-300 leading-relaxed">
          Click <span className="font-semibold text-white">Open Plaid Link</span> to securely connect your bank or brokerage.
          You'll log in directly with your institution — Finovo never sees your password.
        </p>
        {process.env.NODE_ENV !== 'production' && (
          <div className="flex items-start gap-2 p-3 bg-amber-950/30 border border-amber-700/30 rounded-lg">
            <span className="text-amber-400 text-xs flex-shrink-0 mt-0.5">🧪</span>
            <div className="text-[11px] text-amber-200/80">
              <span className="font-semibold">Sandbox mode:</span> Use username{' '}
              <code className="bg-slate-800 px-1 rounded font-mono">user_good</code> and password{' '}
              <code className="bg-slate-800 px-1 rounded font-mono">pass_good</code> to test.
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 text-[10px] text-slate-500 pt-1">
          {['🔒 256-bit encryption', '👁 Read-only access', '🚫 Credentials never stored'].map(t => (
            <span key={t}>{t}</span>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={onBack}
          className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-xl transition-colors">
          ← Back
        </button>
        <button
          onClick={() => open()}
          disabled={!ready}
          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <Link2 size={14} />
          {ready ? 'Open Plaid Link' : 'Loading…'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function AccountLinkModal({ onClose, onAccountAdded }) {
  const { userProfile, updateSection, budgetData, netWorthData, investingData } = useUserData();

  const [phase, setPhase]     = useState('options');  // 'options' | 'plaid' | 'manual'
  const [howOpen, setHowOpen] = useState(false);

  // Plaid Link state
  const [linkToken, setLinkToken]     = useState(null);
  const [plaidStep, setPlaidStep]     = useState('idle'); // 'idle' | 'loading' | 'ready' | 'linking' | 'syncing' | 'success' | 'error'
  const [plaidError, setPlaidError]   = useState('');

  // Manual form state
  const [form, setForm] = useState({ name: '', institution: '', type: 'checking', balance: '' });
  const [holdings, setHoldings]       = useState([]);
  const [hInput, setHInput]           = useState({ ticker: '', shares: '', costBasis: '' });
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Fetch Plaid link token when entering the Plaid phase ─────────────────
  useEffect(() => {
    if (phase !== 'plaid') return;
    setPlaidStep('loading');
    setPlaidError('');
    createLinkToken()
      .then(token => { setLinkToken(token); setPlaidStep('ready'); })
      .catch(err  => { setPlaidError(err.message || 'Could not initialise Plaid Link.'); setPlaidStep('error'); });
  }, [phase]);

  // ── Plaid Link success handler ────────────────────────────────────────────
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setPlaidStep('linking');
    setPlaidError('');
    try {
      // 1. Exchange token — stores access_token server-side
      await exchangeToken(publicToken, metadata);

      // 2. Sync all data (accounts, transactions, investments)
      setPlaidStep('syncing');
      const synced = await syncAllPlaidData({ budgetData, netWorthData, investingData });

      // 3. Merge Plaid accounts into user_profile.linkedAccounts
      const updatedLinked = buildLinkedAccounts(synced.accounts, userProfile?.linkedAccounts || []);
      await updateSection('user_profile', { ...userProfile, linkedAccounts: updatedLinked });

      // 4. Save budget, net worth, investing in parallel
      const saves = [];
      if (synced.newBudget !== budgetData)
        saves.push(updateSection('budget', synced.newBudget));
      if (synced.newNetWorth !== netWorthData)
        saves.push(updateSection('net_worth', synced.newNetWorth));
      if (synced.newInvesting && synced.newInvesting !== investingData)
        saves.push(updateSection('investing', synced.newInvesting));
      await Promise.all(saves);

      setPlaidStep('success');
      onAccountAdded?.({ connectionType: 'plaid', institution: metadata?.institution?.name });
    } catch (err) {
      setPlaidError(err.message || 'Sync failed — please try again.');
      setPlaidStep('error');
    }
  }, [budgetData, netWorthData, investingData, userProfile, updateSection, onAccountAdded]);

  const onPlaidExit = useCallback((err) => {
    if (err) setPlaidError(err.display_message || err.error_message || 'Link was closed.');
    setPlaidStep('ready');
  }, []);

  // ── Save to Supabase (manual accounts) ───────────────────────────────────
  const saveAccount = async (accountData) => {
    setSaving(true);
    setError('');
    try {
      const existing = userProfile?.linkedAccounts || [];
      const newAccount = {
        id: `acc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        ...accountData,
        lastUpdated: new Date().toISOString(),
        manualBannerDismissed: false,
      };
      await updateSection('user_profile', {
        ...userProfile,
        linkedAccounts: [...existing, newAccount],
      });
      onAccountAdded?.(newAccount);
      onClose();
    } catch (err) {
      setError('Failed to save account. Please try again.');
      setSaving(false);
    }
  };

  // ── Manual submit ─────────────────────────────────────────────────────────
  const handleManualSubmit = (e) => {
    e.preventDefault();
    const balance = parseFloat(String(form.balance).replace(/[^0-9.]/g, ''));
    if (!form.name.trim() || isNaN(balance) || balance < 0) {
      setError('Please fill in all required fields.');
      return;
    }
    saveAccount({
      name:         form.name.trim(),
      institution:  form.institution.trim() || form.name.trim(),
      type:         form.type,
      balance,
      connectionType: 'manual',
      holdings:     isInvestmentType(form.type) ? holdings : [],
    });
  };

  // ── Add holding row ───────────────────────────────────────────────────────
  const addHolding = () => {
    const shares    = parseFloat(hInput.shares);
    const costBasis = parseFloat(hInput.costBasis);
    if (!hInput.ticker.trim() || isNaN(shares) || shares <= 0) return;
    setHoldings(h => [...h, {
      ticker:    hInput.ticker.toUpperCase().trim(),
      shares,
      costBasis: isNaN(costBasis) ? 0 : costBasis,
    }]);
    setHInput({ ticker: '', shares: '', costBasis: '' });
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-700/60 sticky top-0 bg-slate-800 z-10">
          <div>
            <h2 className="font-bold text-slate-100 text-lg">
              {phase === 'options' ? 'Add an Account' : phase === 'plaid' ? 'Connect with Plaid' : 'Add Account Manually'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {phase === 'options' ? 'Choose how you want to add your financial account' :
               phase === 'plaid'   ? 'Securely link your bank or brokerage in seconds' :
                                     'Enter your account details — you can update them anytime'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6">

          {/* ── PHASE: Options ─────────────────────────────────────────── */}
          {phase === 'options' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* Option 1 — Plaid */}
                <div className="relative flex flex-col border-2 border-blue-500/60 bg-blue-950/20 rounded-2xl p-5 hover:border-blue-400/80 transition-colors">
                  {/* Recommended badge */}
                  <div className="absolute -top-3 left-4">
                    <span className="flex items-center gap-1 px-2.5 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full shadow-lg uppercase tracking-wide">
                      <Check size={9} /> Recommended
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mb-3 mt-1">
                    {/* Plaid-style logo */}
                    <div className="w-10 h-10 rounded-xl bg-[#111827] flex items-center justify-center flex-shrink-0 border border-slate-600">
                      <span className="text-white font-black text-sm tracking-tight">P</span>
                    </div>
                    <div>
                      <div className="font-bold text-slate-100 text-sm">Link Account Automatically</div>
                      <div className="text-xs text-blue-300 font-medium">Powered by Plaid</div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-400 leading-relaxed flex-1">
                    Securely connect your bank, brokerage, or credit card. Your balances, transactions, and holdings{' '}
                    <span className="text-blue-300 font-medium">update automatically</span> so you always see live data.
                    We use Plaid, the same secure service used by Venmo, Robinhood, and thousands of financial apps.{' '}
                    <span className="text-slate-300">We never see or store your bank login credentials.</span>
                  </p>

                  {/* How it works expandable */}
                  <button
                    onClick={() => setHowOpen(o => !o)}
                    className="flex items-center gap-1.5 mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {howOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    How it works
                  </button>
                  {howOpen && (
                    <div className="mt-2 space-y-1.5 text-xs text-slate-400 bg-slate-900/50 rounded-xl p-3 border border-slate-700/50">
                      {[
                        'You select your bank or brokerage.',
                        'You log in directly with your bank — Finovo never sees your password.',
                        'Plaid creates a secure, read-only connection.',
                        'Your accounts, balances, and transactions sync automatically.',
                        'You can disconnect at any time from Settings.',
                      ].map((step, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="w-4 h-4 rounded-full bg-blue-600/40 text-blue-300 text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => setPhase('plaid')}
                    className="mt-4 w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-900/40"
                  >
                    <Link2 size={14} /> Connect with Plaid
                  </button>
                </div>

                {/* Option 2 — Manual */}
                <div className="flex flex-col border border-slate-700 bg-slate-800/60 rounded-2xl p-5 hover:border-slate-600 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <Pencil size={18} className="text-slate-300" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-200 text-sm">Add Manually</div>
                      <div className="text-xs text-slate-500">Full control</div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-400 leading-relaxed flex-1">
                    Prefer to enter your own numbers? You can manually add accounts, balances, and holdings.{' '}
                    <span className="text-amber-300/80 font-medium">Note:</span> you will need to update these numbers yourself to keep them current.
                  </p>

                  <button
                    onClick={() => setPhase('manual')}
                    className="mt-4 w-full py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <Pencil size={14} /> Add Manually
                  </button>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-6 pt-2">
                {['🔒 256-bit encryption', '👁 Read-only access', '🚫 No credentials stored'].map(t => (
                  <span key={t} className="text-xs text-slate-600">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* ── PHASE: Plaid Link ──────────────────────────────────────── */}
          {phase === 'plaid' && (
            <PlaidPhase
              plaidStep={plaidStep}
              plaidError={plaidError}
              linkToken={linkToken}
              onSuccess={onPlaidSuccess}
              onExit={onPlaidExit}
              onBack={() => { setPhase('options'); setPlaidStep('idle'); setPlaidError(''); }}
              onClose={onClose}
            />
          )}

          {/* ── PHASE: Manual Form ─────────────────────────────────────── */}
          {phase === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block font-medium">Account Name *</label>
                  <input
                    value={form.name}
                    onChange={e => setF('name', e.target.value)}
                    placeholder="e.g. Chase Checking"
                    className={inputCls}
                    autoFocus
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block font-medium">Institution Name</label>
                  <input
                    value={form.institution}
                    onChange={e => setF('institution', e.target.value)}
                    placeholder="e.g. Chase, Fidelity"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block font-medium">Account Type *</label>
                  <select
                    value={form.type}
                    onChange={e => setF('type', e.target.value)}
                    className={inputCls}
                  >
                    {ACCOUNT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block font-medium">Current Balance *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-semibold">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.balance}
                      onChange={e => setF('balance', e.target.value)}
                      placeholder="0.00"
                      className={`${inputCls} pl-7`}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Holdings — only for investment account types */}
              {isInvestmentType(form.type) && (
                <div className="space-y-3 p-4 bg-slate-900/60 rounded-xl border border-slate-700/60">
                  <div className="text-xs font-semibold text-slate-300">
                    Individual Holdings <span className="text-slate-500 font-normal">(optional)</span>
                  </div>

                  {/* Existing holdings */}
                  {holdings.length > 0 && (
                    <div className="space-y-1.5">
                      {holdings.map((h, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-slate-300 bg-slate-800 rounded-lg px-3 py-2">
                          <span className="font-bold text-blue-300 w-14 flex-shrink-0">{h.ticker}</span>
                          <span className="text-slate-500">{h.shares} shares</span>
                          {h.costBasis > 0 && <span className="text-slate-600">@ ${h.costBasis} cost</span>}
                          <button type="button" onClick={() => setHoldings(hs => hs.filter((_, j) => j !== i))}
                            className="ml-auto text-slate-600 hover:text-rose-400 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add holding row */}
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Ticker (AAPL)"
                      value={hInput.ticker}
                      onChange={e => setHInput(h => ({ ...h, ticker: e.target.value.toUpperCase() }))}
                      className={`${inputCls} uppercase`}
                      maxLength={10}
                    />
                    <input
                      type="number"
                      placeholder="Shares"
                      min="0"
                      step="0.0001"
                      value={hInput.shares}
                      onChange={e => setHInput(h => ({ ...h, shares: e.target.value }))}
                      className={inputCls}
                    />
                    <input
                      type="number"
                      placeholder="Cost/share"
                      min="0"
                      step="0.01"
                      value={hInput.costBasis}
                      onChange={e => setHInput(h => ({ ...h, costBasis: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addHolding}
                    disabled={!hInput.ticker.trim() || !hInput.shares}
                    className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus size={12} /> Add holding
                  </button>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-xs text-rose-300 bg-rose-950/30 border border-rose-700/30 rounded-lg px-3 py-2">
                  <AlertCircle size={12} /> {error}
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setPhase('options')}
                  className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-xl transition-colors"
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {saving ? <><RefreshCw size={13} className="animate-spin" /> Saving…</> : <><Check size={14} /> Save Account</>}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
